/**
 * Camadas PAEBM do mapa (M2) — sirenes + geofence ZSS (dados estáticos demo do
 * protótipo). Coordenadas UTM 23S SIRGAS2000.
 *
 * Edificações ZAS + pontos de encontro SAÍRAM daqui para o banco (Supabase+RLS)
 * no GUPM-195 — o que RESOLVEU a barreira LGPD (não há mais PII de morador no
 * bundle). Ver `@/domain/paebm/mapa` (tipos + mappers) e `PaebmDataSource`.
 *
 * Futuro: sirenes via datasource `sirenes` (TCal); ZAS/ZSS via dam break Walm.
 */
import { utmToWgs84 } from './coords';

/** Centróide das barragens (PAEBM Rev.2/2024) — base das geofences. */
export const DAM_CENTER: [number, number] = [-18.380903, -46.92713471];

export type SireneStatus = 'OK' | 'FALHA';

export interface SirenePoint {
  id: string;
  lat: number;
  lon: number;
  status: SireneStatus;
  lastTest: string;
  color: string;
}

const SIRENE_COLOR: Record<SireneStatus, string> = {
  OK: '#22c55e',
  FALHA: '#ef4444',
};

// Sirenes — PAEBM Rev.2/2024 Quadro 8.3.
// GUPM-337 — status NÃO vem de sistema (sirenes não integradas; TCal). A UPM-04 vinha
// do protótipo como FALHA, sem fonte, contra o checklist de plantão (todas ativas).
// Decisão do dono (05/10/2026): todas exibidas como normais até a integração.
const SIRENES_RAW: { id: string; e: number; n: number; status: SireneStatus; lastTest: string }[] = [
  { id: 'UPM-01', e: 295663.0, n: 7966170.0, status: 'OK', lastTest: '05/04/2026' },
  { id: 'UPM-02', e: 293593.0, n: 7965835.0, status: 'OK', lastTest: '05/04/2026' },
  { id: 'UPM-03', e: 291450.0, n: 7966538.0, status: 'OK', lastTest: '05/04/2026' },
  { id: 'UPM-04', e: 292030.0, n: 7965139.0, status: 'OK', lastTest: '05/04/2026' },
  { id: 'UPM-05', e: 290588.0, n: 7967483.0, status: 'OK', lastTest: '05/04/2026' },
  { id: 'UPM-06', e: 291323.93, n: 7968503.15, status: 'OK', lastTest: '05/04/2026' },
];

/** Sirenes ZAS/ZSS — PAEBM Rev.2/2024 Quadro 8.3 (UTM SIRGAS2000 23S). */
export function sirenePoints(): SirenePoint[] {
  return SIRENES_RAW.map((s) => {
    const [lat, lon] = utmToWgs84(s.e, s.n);
    return { id: s.id, lat, lon, status: s.status, lastTest: s.lastTest, color: SIRENE_COLOR[s.status] };
  });
}

/**
 * Caixa envolvente (lat/lon) de um GeoJSON, em ordem Leaflet `[[minLat,minLon],[maxLat,maxLon]]`.
 * GeoJSON usa `[lon, lat]`; percorremos recursivamente todos os vértices.
 * Retorna `null` se não houver coordenada.
 */
export function geoJsonBounds(geojson: unknown): [[number, number], [number, number]] | null {
  let minLat = Infinity;
  let minLon = Infinity;
  let maxLat = -Infinity;
  let maxLon = -Infinity;
  let found = false;

  const visit = (node: unknown): void => {
    if (!Array.isArray(node)) return;
    if (typeof node[0] === 'number' && typeof node[1] === 'number') {
      const [lon, lat] = node as [number, number];
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      found = true;
      return;
    }
    for (const child of node) visit(child);
  };

  if (geojson && typeof geojson === 'object') {
    const fc = geojson as { features?: { geometry?: { coordinates?: unknown } }[] };
    for (const f of fc.features ?? []) visit(f.geometry?.coordinates);
  }

  return found ? [[minLat, minLon], [maxLat, maxLon]] : null;
}

/**
 * Anel (elipse de 64 vértices) placeholder da ZSS — Zona de Salvamento
 * Secundária. A geometria oficial não existe (a fornecer pela Foscentro); o
 * protótipo V8 desenha uma estimativa elíptica ~15 km em volta do centróide.
 * Espelha `ellipsePts(DAM_LAT, DAM_LON, 0.145, 0.137, 64)` do V8.
 * TS puro (regra de domínio #1): sem Leaflet/DOM; devolve pontos `[lat, lon]`.
 */
export function zssPlaceholderRing(): [number, number][] {
  const [lat, lon] = DAM_CENTER;
  const rx = 0.145; // raio em longitude (°)
  const ry = 0.137; // raio em latitude (°)
  const n = 64;
  const pts: [number, number][] = [];
  for (let i = 0; i <= n; i++) {
    const a = (2 * Math.PI * i) / n;
    pts.push([lat + ry * Math.sin(a), lon + rx * Math.cos(a)]);
  }
  return pts;
}
